import {
  useState,
} from "react";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import faqData from "../../../site/data/faq";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  FAQ SECTION
  ============================================================

  Yhteinen UKK-komponentti eri sivuille.

  Ominaisuudet:
  - vain yksi kysymys auki kerrallaan
  - koko rivi klikattava
  - pehmeä avaus / sulkeminen
  - saavutettava aria-expanded / aria-controls
  - FI / EN
  - data erillään komponentista
*/

function localized(
  value,
  language
) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  if (
    typeof value ===
    "string"
  ) {
    return value;
  }

  return (
    value[language] ??
    value.fi ??
    value.en ??
    ""
  );
}

export default function FaqSection({
  language = "fi",
  sectionKey = "sessions",
}) {
  const section =
    faqData?.[
      sectionKey
    ];

  const [
    openId,
    setOpenId,
  ] = useState(null);

  if (
    !section ||
    section.enabled ===
      false
  ) {
    return null;
  }

  const items =
    section.items ||
    [];

  if (
    items.length ===
    0
  ) {
    return null;
  }

  const toggleItem =
    (itemId) => {
      setOpenId(
        (current) =>
          current ===
          itemId
            ? null
            : itemId
      );
    };

  return (
    <section
      className="
        border-b
        border-border
        bg-canvas
      "
    >
      <div
        className="
          mx-auto
          grid
          w-full
          max-w-editorial
          gap-16
          px-6
          py-24

          sm:px-10
          sm:py-32

          lg:grid-cols-[minmax(0,0.7fr)_minmax(480px,1fr)]
          lg:gap-24
          lg:px-16
          lg:py-40
        "
      >
        {/* ====================================================
            INTRO
            ==================================================== */}

        <motion.div
          className="
            lg:sticky
            lg:top-32
            lg:self-start
          "
          initial={{
            opacity: 0,
            y: 24,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.3,
          }}
          transition={{
            duration: 0.85,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <p
            className="
              mb-6
              text-[9px]
              font-medium
              uppercase
              tracking-[0.28em]
              text-muted

              sm:text-[10px]
            "
          >
            {localized(
              section.eyebrow,
              language
            )}
          </p>

          <h2
            className="
              m-0
              max-w-3xl
              font-display
              text-[clamp(3rem,5.5vw,6rem)]
              font-normal
              leading-[0.9]
              tracking-[-0.05em]
              text-text
            "
          >
            {localized(
              section.title,
              language
            )}
          </h2>

          <div
            className="
              my-8
              h-px
              w-10
              bg-text
            "
            aria-hidden="true"
          />

          <p
            className="
              m-0
              max-w-md
              text-sm
              leading-7
              text-muted

              sm:text-base
              sm:leading-8
            "
          >
            {localized(
              section.description,
              language
            )}
          </p>
        </motion.div>

        {/* ====================================================
            ACCORDION
            ==================================================== */}

        <motion.div
          className="
            border-t
            border-border
          "
          initial={{
            opacity: 0,
            y: 24,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.12,
          }}
          transition={{
            duration: 0.85,
            delay: 0.08,
          }}
        >
          {items.map(
            (
              item,
              index
            ) => {
              const isOpen =
                openId ===
                item.id;

              const answerId =
                `faq-answer-${sectionKey}-${item.id}`;

              const buttonId =
                `faq-button-${sectionKey}-${item.id}`;

              return (
                <div
                  key={
                    item.id
                  }
                  className="
                    border-b
                    border-border
                  "
                >
                  <button
                    id={
                      buttonId
                    }
                    type="button"
                    onClick={() =>
                      toggleItem(
                        item.id
                      )
                    }
                    aria-expanded={
                      isOpen
                    }
                    aria-controls={
                      answerId
                    }
                    className="
                      group
                      flex
                      w-full
                      items-start
                      gap-6
                      border-0
                      bg-transparent
                      px-0
                      py-7
                      text-left
                      text-text

                      sm:gap-8
                      sm:py-8
                    "
                  >
                    <span
                      className="
                        mt-[7px]
                        w-7
                        shrink-0
                        text-[9px]
                        font-medium
                        tracking-[0.16em]
                        text-muted

                        sm:w-9
                      "
                      aria-hidden="true"
                    >
                      {String(
                        index + 1
                      ).padStart(
                        2,
                        "0"
                      )}
                    </span>

                    <span
                      className="
                        min-w-0
                        flex-1
                        font-display
                        text-[clamp(1.55rem,2.5vw,2.35rem)]
                        font-normal
                        leading-[1.05]
                        tracking-[-0.025em]
                      "
                    >
                      {localized(
                        item.question,
                        language
                      )}
                    </span>

                    <span
                      className="
                        relative
                        mt-1
                        block
                        h-8
                        w-8
                        shrink-0
                        rounded-full
                        border
                        border-border
                        transition-colors
                        duration-300

                        group-hover:border-text
                      "
                      aria-hidden="true"
                    >
                      <span
                        className="
                          absolute
                          left-1/2
                          top-1/2
                          h-px
                          w-3
                          -translate-x-1/2
                          -translate-y-1/2
                          bg-text
                        "
                      />

                      <span
                        className={`
                          absolute
                          left-1/2
                          top-1/2
                          h-3
                          w-px
                          -translate-x-1/2
                          -translate-y-1/2
                          bg-text
                          transition-opacity
                          duration-200

                          ${
                            isOpen
                              ? "opacity-0"
                              : "opacity-100"
                          }
                        `}
                      />
                    </span>
                  </button>

                  <AnimatePresence
                    initial={false}
                  >
                    {isOpen && (
                      <motion.div
                        id={
                          answerId
                        }
                        role="region"
                        aria-labelledby={
                          buttonId
                        }
                        initial={{
                          height: 0,
                          opacity: 0,
                        }}
                        animate={{
                          height:
                            "auto",
                          opacity: 1,
                        }}
                        exit={{
                          height: 0,
                          opacity: 0,
                        }}
                        transition={{
                          height: {
                            duration:
                              0.4,

                            ease: [
                              0.22,
                              1,
                              0.36,
                              1,
                            ],
                          },

                          opacity: {
                            duration:
                              0.3,
                          },
                        }}
                        className="
                          overflow-hidden
                        "
                      >
                        <div
                          className="
                            pb-8
                            pl-[52px]
                            pr-12

                            sm:pb-10
                            sm:pl-[68px]
                            sm:pr-16
                          "
                        >
                          <p
                            className="
                              m-0
                              max-w-2xl
                              text-sm
                              leading-7
                              text-muted

                              sm:text-base
                              sm:leading-8
                            "
                          >
                            {localized(
                              item.answer,
                              language
                            )}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }
          )}
        </motion.div>
      </div>
    </section>
  );
}